import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

const FIELDS = [
  { key: 'pest365', label: 'Pest365', color: '#2563eb' },
  { key: 'pest365Plus', label: 'Pest365+', color: '#8b5cf6' },
  { key: 'oneTime', label: 'One-Time', color: '#f59e0b' },
  { key: 'initialServiceVisit', label: 'Init. Visit', color: '#16a34a' },
  { key: 'mosquitoTickControl', label: 'Mosquito/Tick', color: '#dc2626' },
];

const CELL_WIDTH = 260;
const CELL_HEIGHT = 180;
const MARGIN = { top: 30, right: 15, bottom: 30, left: 40 };
const COLS = 3;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
};

function drawHistogram(g, values, width, height, color, label) {
  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;

  const chart = g.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

  // Title
  g.append('text')
    .attr('x', width / 2)
    .attr('y', 16)
    .attr('text-anchor', 'middle')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text(label);

  if (!values || values.length === 0) {
    chart
      .append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH / 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#9ca3af')
      .text('No data');
    return;
  }

  const extent = d3.extent(values);
  const x = d3.scaleLinear().domain(extent).nice().range([0, innerW]);

  const numBins = Math.min(Math.max(Math.ceil(Math.sqrt(values.length)), 8), 15);
  const histogram = d3.bin().domain(x.domain()).thresholds(numBins);
  const bins = histogram(values);

  const y = d3.scaleLinear()
    .domain([0, d3.max(bins, (d) => d.length)])
    .nice()
    .range([innerH, 0]);

  // Bars
  chart
    .selectAll('.bar')
    .data(bins)
    .enter()
    .append('rect')
    .attr('x', (d) => x(d.x0) + 1)
    .attr('y', (d) => y(d.length))
    .attr('width', (d) => Math.max(0, x(d.x1) - x(d.x0) - 2))
    .attr('height', (d) => innerH - y(d.length))
    .attr('fill', color)
    .attr('opacity', 0.7)
    .attr('rx', 1);

  // Mean line
  const mean = d3.mean(values);
  chart
    .append('line')
    .attr('x1', x(mean))
    .attr('x2', x(mean))
    .attr('y1', 0)
    .attr('y2', innerH)
    .attr('stroke', '#374151')
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', '4 3');

  chart
    .append('text')
    .attr('x', x(mean) + 4)
    .attr('y', 10)
    .attr('font-size', '9px')
    .attr('fill', '#374151')
    .text(`\u03BC=$${mean.toFixed(0)}`);

  // X axis
  chart
    .append('g')
    .attr('transform', `translate(0, ${innerH})`)
    .call(
      d3.axisBottom(x).ticks(5).tickFormat((v) => `$${v}`)
    )
    .selectAll('text')
    .attr('font-size', '9px')
    .attr('fill', '#6b7280');

  // Y axis
  chart
    .append('g')
    .call(d3.axisLeft(y).ticks(4))
    .selectAll('text')
    .attr('font-size', '9px')
    .attr('fill', '#6b7280');

  // Remove domain lines
  chart.selectAll('.domain').attr('stroke', '#e5e7eb');
  chart.selectAll('.tick line').attr('stroke', '#e5e7eb');
}

function drawMTCPie(g, values, width, height) {
  const chart = g.append('g').attr('transform', `translate(${width / 2}, ${height / 2 + 10})`);

  g.append('text')
    .attr('x', width / 2)
    .attr('y', 16)
    .attr('text-anchor', 'middle')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text('MTC Availability');

  if (!values) {
    chart
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#9ca3af')
      .text('No data');
    return;
  }

  const available = values.filter((v) => typeof v === 'number').length;
  const notAvailable = values.length - available;
  const pieData = [
    { label: 'Available', value: available, color: '#16a34a' },
    { label: 'Not Available', value: notAvailable, color: '#e5e7eb' },
  ].filter((d) => d.value > 0);

  const radius = Math.min(width, height) / 2 - 30;
  const pie = d3.pie().value((d) => d.value).sort(null);
  const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius);

  chart
    .selectAll('.slice')
    .data(pie(pieData))
    .enter()
    .append('path')
    .attr('d', arc)
    .attr('fill', (d) => d.data.color)
    .attr('stroke', '#fff')
    .attr('stroke-width', 2);

  // Center text
  const pct = values.length > 0 ? ((available / values.length) * 100).toFixed(0) : '0';
  chart
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', '-0.1em')
    .attr('font-size', '18px')
    .attr('font-weight', '700')
    .attr('fill', '#374151')
    .text(`${pct}%`);

  chart
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', '1.2em')
    .attr('font-size', '10px')
    .attr('fill', '#9ca3af')
    .text('available');
}

export default function DistributionChart({ data }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // 5 histograms + 1 MTC pie = 6 cells, 2 rows x 3 cols
    const rows = 2;
    const totalWidth = COLS * CELL_WIDTH;
    const totalHeight = rows * CELL_HEIGHT;

    svg.attr('width', totalWidth).attr('height', totalHeight);

    // Draw histograms for first 4 fields
    FIELDS.slice(0, 4).forEach((field, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const g = svg
        .append('g')
        .attr('transform', `translate(${col * CELL_WIDTH}, ${row * CELL_HEIGHT})`);

      const values = data[field.key];
      const numericValues = values
        ? values.filter((v) => v != null && typeof v === 'number')
        : [];

      drawHistogram(g, numericValues, CELL_WIDTH, CELL_HEIGHT, field.color, field.label);
    });

    // MTC histogram (index 4, position row=1 col=1)
    const mtcField = FIELDS[4];
    const mtcG = svg
      .append('g')
      .attr('transform', `translate(${1 * CELL_WIDTH}, ${1 * CELL_HEIGHT})`);
    const mtcNumeric = data[mtcField.key]
      ? data[mtcField.key].filter((v) => v != null && typeof v === 'number')
      : [];
    drawHistogram(mtcG, mtcNumeric, CELL_WIDTH, CELL_HEIGHT, mtcField.color, mtcField.label);

    // MTC availability pie (position row=1 col=2)
    const pieG = svg
      .append('g')
      .attr('transform', `translate(${2 * CELL_WIDTH}, ${1 * CELL_HEIGHT})`);
    drawMTCPie(pieG, data.mosquitoTickControlAll || data[mtcField.key], CELL_WIDTH, CELL_HEIGHT);

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Distributions</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No distribution data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Distributions</div>
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>
    </div>
  );
}
